import React, { useState, useEffect, useCallback } from 'react';
import { AppTheme } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import './Form.css';

interface LoginFormProps {
  theme: AppTheme;
  onLogin: (username: string, password: string) => void;
  error?: string;
  onToggleTheme: () => void;
}

// Функция для генерации UUID
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const LoginForm: React.FC<LoginFormProps> = ({ theme, onLogin, error, onToggleTheme }) => {
  const { language, setLanguage, t } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [sessionId, setSessionId] = useState(generateUUID());
  const [isMobile, setIsMobile] = useState(
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768
  );

  // Отслеживаем изменение размера окна
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      console.log('Window width:', window.innerWidth, 'isMobile:', mobile);
      setIsMobile(mobile);
    };

    // Проверяем сразу при загрузке
    checkMobile();

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Генерируем QR-код с session_id
  const generateQRCode = useCallback((sid: string) => {
    const qrData = encodeURIComponent(JSON.stringify({
      session_id: sid
    }));
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=003497&data=${qrData}`;
  }, []);

  const [qrCode, setQrCode] = useState(generateQRCode(sessionId));

  // Таймер для QR кода
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Функция для генерации нового QR
  const handleNewQR = () => {
    const newSessionId = generateUUID();
    setSessionId(newSessionId);
    setQrCode(generateQRCode(newSessionId));
    setTimeLeft(60);
  };

  // Определяем цвет таймера
  const getTimerColor = () => {
    if (timeLeft <= 10) return '#dc3545'; // красный
    if (timeLeft <= 42) return '#ff9800'; // оранжевый
    return theme.isDark ? '#ffffff' : '#000000'; // обычный цвет
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!showOtp && username && password) {
      // После ввода логина и пароля показываем поле OTP
      setShowOtp(true);
    } else if (showOtp && otp) {
      // Если OTP введен, выполняем вход
      onLogin(username, password);
    }
  };

  console.log('LoginForm render - isMobile:', isMobile, 'window.innerWidth:', window.innerWidth);

  return (
    <div className={`login-container ${theme.isDark ? 'dark' : 'light'}`}>
      <div className="login-controls">
        <button
          className={`control-button ${theme.isDark ? 'dark' : 'light'}`}
          onClick={onToggleTheme}
          type="button"
        >
          {theme.isDark ? '☀️' : '🌙'}
        </button>
        <button
          className={`control-button ${theme.isDark ? 'dark' : 'light'}`}
          onClick={() => setLanguage(language === 'ru' ? 'ky' : language === 'ky' ? 'en' : 'ru')}
          type="button"
        >
          {language === 'ru' ? 'КЫР' : language === 'ky' ? 'ENG' : 'РУС'}
        </button>
      </div>
      <div className="login-header-container">
        <h1 className="login-main-title">{t('app.title')}</h1>
        <p className="login-subtitle">{t('app.subtitle')}</p>
      </div>
      <div className="login-wrapper">
        <form className={`login-form ${theme.isDark ? 'dark' : 'light'}`} onSubmit={handleSubmit}>
          <h2 className={`login-title ${theme.isDark ? 'dark' : 'light'}`}>{t('login.title')}</h2>

          {error && <div className="login-error">{error}</div>}

          {!showOtp ? (
            <>
              <div className="login-input-group">
                <label className={`login-label ${theme.isDark ? 'dark' : 'light'}`} htmlFor="username">
                  {t('login.username')}
                </label>
                <input
                  className={`login-input ${theme.isDark ? 'dark' : 'light'}`}
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('placeholder.username')}
                  required
                />
              </div>

              <div className="login-input-group">
                <label className={`login-label ${theme.isDark ? 'dark' : 'light'}`} htmlFor="password">
                  {t('login.password')}
                </label>
                <input
                  className={`login-input ${theme.isDark ? 'dark' : 'light'}`}
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('placeholder.password')}
                  required
                />
              </div>
            </>
          ) : (
            <div className="login-input-group">
              <label className={`login-label ${theme.isDark ? 'dark' : 'light'}`} htmlFor="otp">
                {t('login.otp')}
              </label>
              <input
                className={`login-input ${theme.isDark ? 'dark' : 'light'}`}
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder={t('placeholder.otp')}
                maxLength={6}
                required
                autoFocus
              />
            </div>
          )}

          <button className="login-button" type="submit">
            {showOtp ? t('login.confirm') : t('login.button')}
          </button>

          {showOtp && (
            <button
              className="login-button-back"
              type="button"
              onClick={() => {
                setShowOtp(false);
                setOtp('');
              }}
            >
              {t('login.back')}
            </button>
          )}

          <div className={`login-hint ${theme.isDark ? 'dark' : 'light'}`}>
            <strong>{t('login.hint')}</strong><br />
            {t('login.username')}: admin<br />
            {t('login.password')}: admin
          </div>
        </form>

        {!isMobile && (
          <div className={`login-qr-section ${theme.isDark ? 'dark' : 'light'}`}>
            <div className={`login-qr-title ${theme.isDark ? 'dark' : 'light'}`}>
              {t('login.qr.title')}
            </div>
            <div className="login-qr-container">
              {timeLeft > 0 ? (
                <img src={qrCode} alt="QR код для входа" className="login-qr-image" />
              ) : (
                <button
                  className="login-button"
                  onClick={handleNewQR}
                  type="button"
                >
                  {t('login.qr.new')}
                </button>
              )}
            </div>
            {timeLeft > 0 ? (
              <div
                className={`login-qr-timer ${theme.isDark ? 'dark' : 'light'}`}
                style={{ color: getTimerColor() }}
              >
                {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
              </div>
            ) : null}
            <div className={`login-qr-hint ${theme.isDark ? 'dark' : 'light'}`}>
              {t('login.qr.hint')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};